<template>
    <div ref="rootElement" class="Select" v-bind="scope.select.attributes">
        <div class="Select-input" v-bind="scope.select.toggle.attributes" v-on="scope.select.toggle.listeners">
            <div ref="selectedOptions" class="Select-input-selected-options">
                <slot name="selected-option-container" v-for="( option, index ) in selectedValue" v-bind="selectedOptionsAttributes( option, index )">
                    <span class="Select-input-selected-option" :key="getOptionKey(option)">
                        <slot name="selected-option" v-bind="selectedOptionAttributes( option, index )">
                            <Tag :class="`Select-input-selected-option-tag primary ${this.class?.split(' ')?.find( f => ['sm','md','lg'].includes( f ) )}`" v-bind="selectedOptionAttributes( option, index )" @on:remove="deselect(option)">
                                {{selectedOptionAttributes( option, index )['label']}}
                            </Tag>
                        </slot>
                    </span>
                </slot>

                <slot name="select-search-input" v-bind="scope.select.toggle.search">
                    <input class="Select-input-search" v-bind="scope.select.toggle.search.attributes" v-on="scope.select.toggle.search.listeners" />
                </slot>
            </div>

            <div class="Select-input-actions">
                <div
                    v-show="showClearButton"

                    class="Select-input-action-clear Anchor"

                    v-bind="scope.select.toggle.actions.deselectIndicator.attributes"
                    v-on="scope.select.toggle.actions.deselectIndicator.listeners"
                >
                    clear
                </div>

                <div
                    v-show="showIndicator"

                    class="Select-input-action-open"

                    v-bind="scope.select.toggle.actions.openIndicator.attributes"
                >
                    <i class="isax isax-arrow-down-1"></i>
                </div>

                <div
                    v-show="mutableLoading"

                    class="Select-input-action-load fa-3x"

                    v-bind="scope.select.toggle.actions.spinnerIndicator.attributes"
                >
                    <i class="fa-solid fa-spinner fa-spin"></i>
                </div>
            </div>
        </div>

        <transition :name="transition" mode="out-in">
            <slot name="select-dropdown-container" v-bind="scope.select.dropdown">
                <ul class="Select-dropdown scroll-content" v-if="dropdownOpen" v-bind="scope.select.dropdown.attributes" v-on="scope.select.dropdown.listeners" v-append-to-body>
                    <li v-if="$slots['select-dropdown-header']">
                        <slot name="select-dropdown-header"></slot>
                    </li>

                    <li class="Select-dropdown-option" v-for="( option, index ) in filteredOptions" v-bind="optionListAttributes( option, index )" v-on="optionListListeners( option, index )">
                        <slot name="select-dropdown-option" v-bind="normalizeOptionForSlot(option)">
                            {{ getOptionLabel(option) }}
                        </slot>
                    </li>

                    <li v-if="filteredOptions.length === 0" class="Select-dropdown-option Select-dropdown-option-empty">
                        <slot name="no-options" v-bind="scope.select.dropdown.options.empty.attributes">Sorry, no matching options.</slot>
                    </li>

                    <li v-if="$slots['select-dropdown-footer']">
                        <slot name="select-dropdown-footer"></slot>
                    </li>
                </ul>
            </slot>
        </transition>
    </div>
</template>

<script>
import props    from './utils/props.js';
import emits    from './utils/emits.js';
import watches  from './utils/watches.js';
import methods  from './utils/methods.js';
import computed from './utils/computed.js';

import appendToBody from './../../../directives/appendToBody';

export default
{
    name            : "Select",
    inheritAttrs    : false,
    directives      : { appendToBody },
    mixins          : [ props, computed, methods, emits, watches ],

    data()
    {
        return (
        {
            search           : '',
            open             : false,
            toggleTimer      : 0,
            isComposing      : false,
            pushedTags       : [],
            _value           : [],
            deselectButtons  : [],
            typeAheadPointer : -1,
            mutableLoading   : false
        })
    },

    created()
    {
        this.mutableLoading = this.loading;
    }
}
</script>

<style lang="scss">
body
{
    &[data-theme="light"]
    {
        .Select-dropdown
        {
            --select-dropdown-border-radius: 4px;
            --select-dropdown-border: 0px solid var(--layout-border-color);

            --select-dropdown-background-color: var(--layout-secondary-background-color);

            --select-dropdown-box-shadow: var(--elevation-lg-shadow);
            --select-dropdown-backdrop-filter: var(--elevation-lg-backdrop);

            --input-select-option-text-color: var(--secondary-text-color);
            --input-select-option-border-color: transparent;
            --input-select-option-background-color: transparent;

            --input-select-option-hover-text-color: var(--primary-text-color);
            --input-select-option-hover-border-color: transparent;
            --input-select-option-hover-background-color: var(--light-surfaces-off-white);

            --input-select-option-focus-text-color: var(--primary-text-color);
            --input-select-option-focus-border-color: var(--primary-colors-blue-60);
            --input-select-option-focus-background-color: var(--primary-colors-blue-10);

            --input-select-option-selected-text-color: var(--primary-text-color);
            --input-select-option-selected-border-color: transparent;
            --input-select-option-selected-background-color: var(--primary-colors-blue-10);
            --input-select-option-selected-line-color: var(--primary-colors-blue-60);

            --input-select-option-deselect-text-color: var(--primary-text-color);
            --input-select-option-deselect-border-color: transparent;
            --input-select-option-deselect-background-color: var(--error-colors-error-10);
            --input-select-option-deselect-line-color: var(--error-colors-error-40);

            --input-select-option-disabled-text-color: var(--disabled-text-color);
            --input-select-option-disabled-border-color: transparent;
            --input-select-option-disabled-background-color: transparent;
        }
    }

    &[data-theme="dark"]
    {
        .Select-dropdown
        {
            --select-dropdown-border-radius: 4px;
            --select-dropdown-border: 1px solid var(--layout-border-color);

            --select-dropdown-background-color: var(--dark-surfaces-gunmetal);

            --select-dropdown-box-shadow: var(--elevation-lg-shadow);
            --select-dropdown-backdrop-filter: var(--elevation-lg-backdrop);

            --input-select-option-text-color: var(--secondary-text-color);
            --input-select-option-border-color: transparent;
            --input-select-option-background-color: transparent;

            --input-select-option-hover-text-color: var(--primary-text-color);
            --input-select-option-hover-border-color: transparent;
            --input-select-option-hover-background-color: var(--layout-primary-background-color);

            --input-select-option-focus-text-color: var(--primary-text-color);
            --input-select-option-focus-border-color: var(--primary-colors-blue-60);
            --input-select-option-focus-background-color: var(--primary-colors-blue-10);

            --input-select-option-selected-text-color: var(--primary-text-color);
            --input-select-option-selected-border-color: transparent;
            --input-select-option-selected-background-color: rgba( 38, 39, 51, 0.80 );
            --input-select-option-selected-line-color: var(--primary-colors-blue-60);

            --input-select-option-deselect-text-color: var(--primary-text-color);
            --input-select-option-deselect-border-color: transparent;
            --input-select-option-deselect-background-color: rgba( 38, 39, 51, 0.80 );
            --input-select-option-deselect-line-color: var(--error-colors-error-40);

            --input-select-option-disabled-text-color: var(--disabled-text-color);
            --input-select-option-disabled-border-color: transparent;
            --input-select-option-disabled-background-color: transparent;
        }
    }
}

.slide-fade-enter-active { transition: all 300ms cubic-bezier(0, 0, 0.2, 1); }
.slide-fade-leave-active { transition: all 200ms cubic-bezier(0.4, 0, 1, 1); }

.slide-fade-enter-from,
.slide-fade-leave-to
{
    transform: translateY(#{4});
    opacity: 0;
}

.Select
{
    position: relative;

    .Select-input
    {
        display: flex;
        align-items: center;
        justify-content: center;

        width: 100%;
        height: 100%;

        gap: px(4);

        .Select-input-selected-options
        {
            position: relative;

            display: flex;
            align-items: center;
            flex-wrap: wrap;
            flex-basis: 100%;
            flex-grow: 1;

            gap: px(4);

            .Select-input-selected-option
            {
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .Select-input-search
            {
                flex-grow: 1;

                width: 0;
                max-width: 100%;

                height: auto;

                margin: 0;
                padding: 0;

                border: unset;
                outline: unset;
                box-shadow: unset;
                background: unset;

                appearance: none;

                z-index: 1;

                &[ readonly ]
                {
                    cursor: pointer;
                }
            }
        }

        .Select-input-actions
        {
            display: flex;
            align-items: center;
            gap: px(8);

            height: 100%;

            cursor: pointer;

            > div
            {
                @include font-body-xs-regular;

                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;

                > i
                {
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    width: px(20);
                    height: px(20);

                    font-size: px(20);
                    line-height: px(20);
                }

                &.Select-input-action-clear
                {
                    padding: 0;
                }

                &.Select-input-action-load
                {

                }

                &.Select-input-action-open
                {
                    transform: scale( 1 );

                    transition: transform 250ms ease-in-out;
                }
            }
        }
    }

    &.vs--open
    {
        .Select-input
        {
            .Select-input-actions
            {
                .Select-input-action-open
                {
                    transform: rotate( 180deg );
                }
            }
        }
    }

    &.vs--single
    {
        .Select-input-selected-option
        {
            .Select-input-selected-option-tag
            {

            }
        }

        &.vs--searchable
        {
            .Select-input-selected-option
            {
                .Select-input-selected-option-tag
                {
                    cursor: text;
                }
            }
        }

        &.vs--unsearchable
        {
            .Select-input-selected-option
            {
                .Select-input-selected-option-tag
                {
                    cursor: pointer;
                }
            }
        }

        &.vs--open:not(.vs--unsearchable) .Select-input-selected-option,
        &.vs--loading .Select-input-selected-option
        {
            position: absolute;
            opacity: 0.4;
            z-index: 2;
        }

        &.vs--searching .Select-input-selected-option
        {
            display: none;
        }

        .vs--searching:not(.vs--open):not(.vs--loading)
        {
            .Select-input-search
            {
                opacity: 0.2;
            }
        }
    }

    &.vs--multiple
    {
        //TODO
    }

    &.vs--searching
    {
        //TODO
    }

    &.vs--searchable
    {
        .Select-input
        {
            cursor: text;
        }
    }

    &.vs--unsearchable
    {
        .Select-input
        {
            cursor: pointer;
        }
    }

    &.vs--loading
    {
        .Select-input-actions
        {
            .Select-input-action-clear
            {}

            .Select-input-action-load
            {
                opacity: 1;
            }

            .Select-input-action-open
            {
                opacity: 0;
            }
        }
    }

    &.vs--disabled
    {

    }

    &[dir='rtl']
    {
        .Select-input-actions {}
        .Select-input-action-clear {}
        .Select-input-selected-option {}
    }
}

.Select-dropdown
{
    --select-height : #{px(264)};

    position: absolute;

    display: flex;
    flex-direction: column;

    width: max-content;
    max-height: var(--select-height);

    padding: 0;
    margin: 0;

    padding-left: px(8);
    padding-right: px(4);

    border-radius: var(--select-dropdown-border-radius);
    border: var(--select-dropdown-border);
    background-color: var(--select-dropdown-background-color);
    backdrop-filter: var(--select-dropdown-backdrop-filter);
    box-shadow: var(--select-dropdown-box-shadow);

    user-select: none;

    overflow-x: hidden;
    overflow-y: scroll;

    z-index: 9999;

    &.bottom
    {
        margin-bottom: px(4);
    }

    &.top
    {
        margin-top: px(4);
    }

    .Select-dropdown-option
    {
        @include font-body-md-regular;

        position: relative;

        display: flex;
        align-items: center;
        align-self: stretch;

        width: 100%;

        padding: px(12) px(8);

        color: var(--input-select-option-text-color);
        border: 1px solid var(--input-select-option-border-color);
        background-color: var(--input-select-option-background-color);

        border-radius: 4px;

        cursor: pointer;

        &.vs-option--highlight
        {
            color: var(--input-select-option-hover-text-color);
            border: 1px solid var(--input-select-option-hover-border-color);
            background-color: var(--input-select-option-hover-background-color);
        }

        &.vs-option--selected
        {
            color: var(--input-select-option-selected-text-color);
            border: 1px solid var(--input-select-option-selected-border-color);
            background-color: var(--input-select-option-selected-background-color);

            &:before
            {
                position: absolute;

                top: 0;
                left: px(-8);

                width: 3px;
                height: 100%;

                content: '';

                border-radius: 0 4px 4px 0;
                background: var(--input-select-option-selected-line-color);
            }
        }

        &:focus
        {
            color: var(--input-select-option-focus-text-color);
            border: 1px dashed var(--input-select-option-focus-border-color);
            background-color: var(--input-select-option-focus-background-color);
        }

        &.vs-option--deselect
        {
            color: var(--input-select-option-deselect-text-color);
            border: 1px solid var(--input-select-option-deselect-border-color);
            background-color: var(--input-select-option-deselect-background-color);

            &:before
            {
                background-color: var(--input-select-option-deselect-line-color);
            }
        }

        &.vs-option--disabled
        {
            color: var(--input-select-option-disabled-text-color);
            border: 1px solid var(--input-select-option-disabled-border-color);
            background-color: var(--input-select-option-disabled-background-color);

            cursor: not-allowed;
        }
    }

    .Select-dropdown-option-empty
    {
        align-items: center;
        justify-content: center;
    }
}
</style>